import FontAwesomeIcon from 'src/components/Icon';
import Button from 'src/components/Button';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import React, { ChangeEvent, ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import './ConfirmationModal.scss';

interface ConfirmationModalProps {
  title: ReactNode;
  message: ReactNode;
  warningMessage?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  variant?: 'warning' | 'danger' | 'info' | 'confirmation';
  requireTypeToConfirm?: boolean;
  confirmationWord?: string;
  children?: ReactNode;
  className?: string;
}

const CANCEL_TEXT_COLOR = '#374151';
const DANGER_BG = '#DC2626';
const DANGER_BG_DISABLED = '#feb2b2';

const CANCEL_BUTTON_STYLE: React.CSSProperties = {
  backgroundColor: 'transparent',
  border: 'none',
  color: CANCEL_TEXT_COLOR,
  textDecoration: 'none',
  boxShadow: 'none',
  fontWeight: 500,
};

const ConfirmationModal = ({
  title,
  message,
  warningMessage,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  isSubmitting = false,
  variant = 'warning',
  requireTypeToConfirm = false,
  confirmationWord = 'DELETE',
  children,
  className = '',
}: ConfirmationModalProps) => {
  const [userInput, setUserInput] = useState('');

  const iconMap = {
    warning: 'exclamation-triangle',
    danger: 'trash',
    info: 'info-circle',
    confirmation: 'info-circle',
  };

  const isConfirmDisabled: boolean =
    isSubmitting || (requireTypeToConfirm && userInput !== confirmationWord);

  const modalContent: React.JSX.Element = (
    <div className="bulk-confirmation-overlay">
      <div className={`bulk-confirmation-card ${className}`}>
        <div className="confirmation-header">
          <FontAwesomeIcon
            glyph={iconMap[variant]}
            weight="solid"
            className={`${variant}-icon`}
            size="medium"
          />
          <h3>{title}</h3>
        </div>

        <div className="confirmation-main-text">{message}</div>
        {requireTypeToConfirm && (
          <div className="confirmation-input-section">
            <p>
              Please type <strong>{confirmationWord}</strong> in all caps to
              confirm.
            </p>
            <input
              type="text"
              className={`confirmation-text-input confirmation-text-input--${variant}`}
              value={userInput}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setUserInput(e.target.value)
              }
              placeholder={`Type ${confirmationWord}`}
              autoFocus
            />
          </div>
        )}

        {warningMessage && (
          <div className={`warning-box warning-box--${variant}`}>
            <FontAwesomeIcon
              glyph="exclamation-circle"
              weight="solid"
              className="warning-box-icon"
            />
            <p>{warningMessage}</p>
          </div>
        )}

        {children}

        <div className="confirmation-actions">
          <Button
            label={cancelLabel}
            color={undefined}
            style={CANCEL_BUTTON_STYLE}
            size="small"
            onClick={onCancel}
            disabled={isSubmitting}
          />
          <LoadingButton
            actionText={confirmLabel}
            isSubmitting={isSubmitting}
            disabled={isConfirmDisabled}
            onClick={onConfirm}
            size="small"
            style={
              variant === 'danger'
                ? {
                    backgroundColor: isConfirmDisabled
                      ? DANGER_BG_DISABLED
                      : DANGER_BG,
                  }
                : undefined
            }
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ConfirmationModal;
